/** @format */

import React, { useRef, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";

// Read a CSS variable straight from the event's custom_css. HeadInjector adds
// that CSS via react-helmet asynchronously, so when the iframe is written the
// page's computed --text-color etc. are often still the defaults.
function extractCssVar(cssString, name) {
	const match = (cssString || "").match(new RegExp(`(?:^|[\\s;{])${name}:\\s*([^;}]+)`));
	return match ? match[1].trim() : null;
}

const IframeContent = ({ children }) => {
	const iframeRef = useRef(null);
	const { custom_css } = useSelector(state => state.config);

	const fontFamily = extractCssVar(custom_css, "--font-family");

	// Get CSS custom properties from the root element
	const getCSSCustomProperties = useCallback(() => {
		const rootStyles = getComputedStyle(document.documentElement);
		const read = (name, fallback) =>
			extractCssVar(custom_css, name) || rootStyles.getPropertyValue(name).trim() || fallback;
		const textColor = read("--text-color", "#333");
		const bgColor = read("--bg-color", "transparent");
		const linkColor = read("--link-color", "#007bff");
		const linkHoverColor = read("--link-hover-color", "#0056b3");
		const inputBgColor = read("--input-bg-color", "#fff");
		const borderColor = read("--border-color", "#ccc");

		// Determine appropriate code background based on text color
		let codeBgColor = read("--code-bg-color", "");
		if (!codeBgColor) {
			// Auto-detect based on text color brightness
			const isLightText = isColorLight(textColor);
			codeBgColor = isLightText ? "rgba(255, 255, 255, 0.1)" : "#f5f5f5";
		}

		return {
			textColor,
			bgColor,
			linkColor,
			linkHoverColor,
			inputBgColor,
			borderColor,
			codeBgColor,
		};
	}, [custom_css]);

	// Helper function to determine if a color is light or dark
	const isColorLight = color => {
		// Convert color to RGB values
		const hex = color.replace("#", "");
		if (hex.length === 3) {
			const r = parseInt(hex[0] + hex[0], 16);
			const g = parseInt(hex[1] + hex[1], 16);
			const b = parseInt(hex[2] + hex[2], 16);
			return r * 0.299 + g * 0.587 + b * 0.114 > 186;
		} else if (hex.length === 6) {
			const r = parseInt(hex.substring(0, 2), 16);
			const g = parseInt(hex.substring(2, 4), 16);
			const b = parseInt(hex.substring(4, 6), 16);
			return r * 0.299 + g * 0.587 + b * 0.114 > 186;
		} else if (hex.length === 8) {
			// Handle 8-character hex colors (RRGGBBAA)
			const r = parseInt(hex.substring(0, 2), 16);
			const g = parseInt(hex.substring(2, 4), 16);
			const b = parseInt(hex.substring(4, 6), 16);
			// Ignore alpha channel for brightness calculation
			return r * 0.299 + g * 0.587 + b * 0.114 > 186;
		}
		// For rgba, rgb, or other formats, assume it's dark text
		return color.includes("white") || color.includes("#fff") || color.includes("255, 255, 255");
	};

	useEffect(() => {
		const adjustIframeHeight = () => {
			if (iframeRef.current) {
				const iframe = iframeRef.current;
				const doc = iframe.contentDocument || iframe.contentWindow.document;

				if (doc && doc.body) {
					iframe.style.height = `${doc.body.scrollHeight}px`; // This does not work correctly if the description is short, needs more work
				}
			}
		};

		if (iframeRef.current) {
			const doc = iframeRef.current.contentDocument || iframeRef.current.contentWindow.document;
			const cssProps = getCSSCustomProperties();

			if (doc) {
				doc.open();
				doc.write(`
					<html>
            <head>
              <style>
                html, body {
                  font-family: ${fontFamily}, sans-serif;
                  margin: 0;
									padding: 0;
									border: 0;
									width: 100%;
									min-height: 0;
									color: ${cssProps.textColor};
									background-color: ${cssProps.bgColor};
                }
                
                /* Apply color to all elements */
                * {
                  color: ${cssProps.textColor} !important;
                  font-family: inherit !important;
                }
                
                /* Specific element styling */
                p, div, span, h1, h2, h3, h4, h5, h6, 
                ul, ol, li, strong, em, code, pre {
                  color: ${cssProps.textColor} !important;
                  font-family: ${fontFamily}, sans-serif !important;
                  margin: 0.5em 0;
                }
                
                /* Headings hierarchy */
                h1 { font-size: 2em; font-weight: bold; font-family: ${fontFamily}, sans-serif !important; }
                h2 { font-size: 1.5em; font-weight: bold; font-family: ${fontFamily}, sans-serif !important; }
                h3 { font-size: 1.3em; font-weight: bold; font-family: ${fontFamily}, sans-serif !important; }
                h4 { font-size: 1.1em; font-weight: bold; font-family: ${fontFamily}, sans-serif !important; }
                h5 { font-size: 1em; font-weight: bold; font-family: ${fontFamily}, sans-serif !important; }
                h6 { font-size: 0.9em; font-weight: bold; font-family: ${fontFamily}, sans-serif !important; }
                
                /* Link styling */
                a {
                  color: ${cssProps.linkColor} !important;
                  font-family: ${fontFamily}, sans-serif !important;
                  text-decoration: underline;
                }
                
                a:hover {
                  color: ${cssProps.linkHoverColor} !important;
                }
                
                /* Form elements */
                input, textarea, select, button {
                  color: ${cssProps.textColor} !important;
                  background-color: ${cssProps.inputBgColor} !important;
                  border: 1px solid ${cssProps.borderColor} !important;
                  font-family: inherit !important;
                }
                
                /* List styling */
                ul, ol {
                  padding-left: 1.5em;
                  color: ${cssProps.textColor} !important;
                  font-family: ${fontFamily}, sans-serif !important;
                }
                
                li {
                  margin: 0.25em 0;
                  font-family: ${fontFamily}, sans-serif !important;
                }
                
                /* Code blocks */
                code, pre {
                  background-color: ${cssProps.codeBgColor} !important;
                  color: ${cssProps.textColor} !important;
                  font-family: 'Courier New', Consolas, Monaco, monospace !important;
                  padding: 0.2em 0.4em;
                  border-radius: 3px;
                }
                
                pre {
                  padding: 1em;
                  overflow-x: auto;
                }
                
                /* Highlight elements - always black text for readability */
                mark {
                  background-color: #ffff00 !important;
                  color: #000 !important;
                  font-family: ${fontFamily}, sans-serif !important;
                  padding: 0.1em 0.2em;
                }
                
                /* Table styling */
                table {
                  border-collapse: collapse;
                  width: 100%;
                  color: ${cssProps.textColor} !important;
                  font-family: ${fontFamily}, sans-serif !important;
                }
                
                th, td {
                  border: 1px solid ${cssProps.borderColor} !important;
                  padding: 0.5em;
                  text-align: left;
                  color: ${cssProps.textColor} !important;
                  font-family: ${fontFamily}, sans-serif !important;
                }
                
                th {
                  background-color: ${cssProps.codeBgColor} !important;
                  font-weight: bold;
                }
              </style>
            </head>
          <body>${children}</body>
        </html>
				`);
				doc.close();

				// Adjust height after content is written
				adjustIframeHeight();
			}
		}

		// Adjust height if iframe content changes dynamically
		const resizeObserver = new ResizeObserver(adjustIframeHeight);
		const iframeBody = iframeRef.current?.contentDocument?.body;
		if (iframeBody) resizeObserver.observe(iframeBody);

		return () => {
			if (iframeBody) resizeObserver.unobserve(iframeBody);
		};
	}, [children, fontFamily, getCSSCustomProperties]);

	return (
		<iframe
			ref={iframeRef}
			style={{ width: "100%", border: "none", overflow: "hidden" }}
			className="editor-iframe"
			title="Rich Text Content"
		/>
	);
};

const RichTextEditorWrapper = ({ children }) => {
	if (!children) return children;
	return <IframeContent>{children}</IframeContent>;
};

export default RichTextEditorWrapper;
